/**
 * @file Hooks for the two cross-surface list events.
 *
 * Search boxes and filter strips look the same on every list page, so they
 * share one event each (`list_searched`, `list_filter_changed`) discriminated
 * by `surface`. These hooks are the only intended way to fire them — they keep
 * the payload shape and the debounce policy in one place instead of repeated
 * across nine call sites.
 */
import { useCallback, useEffect, useRef } from 'react';
import { useTrack } from './useTrack';
import {
  ANALYTICS_EVENTS,
  type FeatureArea,
  type InteractionVia,
  type ListFilterKind,
  type ListSurface,
} from './events';

/**
 * How long typing must pause before a search counts. Long enough that a
 * half-typed word doesn't settle on its own.
 */
export const SEARCH_TRACK_DEBOUNCE_MS = 700;

export interface TrackListSearchOptions {
  surface: ListSurface;
  featureArea: FeatureArea;
  /** The live input value. */
  query: string;
  /** Rows matching the query, when the caller knows it. */
  resultCount?: number;
  /** How the box was reached — set to `'shortcut'` when ⌘K focused it. */
  via?: InteractionVia;
}

/**
 * Fires `list_searched` once per search session — a contiguous run of
 * non-empty input — rather than once per keystroke pause. The question this
 * feeds is "does anyone search here", so sessions are the honest unit; counting
 * pauses would inflate a five-word query into five searches.
 *
 * The session resets when the box is emptied, so searching again counts again.
 * The query text is never sent, only whether it matched anything.
 */
export function useTrackListSearch({
  surface,
  featureArea,
  query,
  resultCount,
  via,
}: TrackListSearchOptions): void {
  const trackEvent = useTrack();
  const trackedThisSession = useRef(false);

  useEffect(() => {
    const trimmed = query.trim();

    if (!trimmed) {
      trackedThisSession.current = false;
      return;
    }
    if (trackedThisSession.current) return;

    const timer = setTimeout(() => {
      trackedThisSession.current = true;
      trackEvent(ANALYTICS_EVENTS.LIST_SEARCHED, {
        surface,
        feature_area: featureArea,
        via,
        ...(resultCount === undefined
          ? {}
          : { result_count: resultCount, has_results: resultCount > 0 }),
      });
    }, SEARCH_TRACK_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [query, resultCount, surface, featureArea, via, trackEvent]);
}

/**
 * Returns a `(filter, value)` callback that reports a filter/sort/tab change
 * for one surface. `value` must stay low-cardinality — a closed set of option
 * ids, never free text.
 */
export function useTrackListFilter(
  surface: ListSurface,
  featureArea: FeatureArea,
): (filter: ListFilterKind, value: string) => void {
  const trackEvent = useTrack();

  return useCallback(
    (filter: ListFilterKind, value: string) => {
      trackEvent(ANALYTICS_EVENTS.LIST_FILTER_CHANGED, {
        surface,
        feature_area: featureArea,
        filter,
        value,
      });
    },
    [trackEvent, surface, featureArea],
  );
}
